import React, { useEffect, useMemo, useState } from 'react';
import { Phone, MessageCircle, RefreshCw, Search, Filter, Users, Inbox, ExternalLink } from 'lucide-react';
import { dealerLeads } from '../../services/dealerApi';
import { formatUtcDatetime } from '../../utils/formatUtcDate';

const formatDate = (iso) => formatUtcDatetime(iso, '—');

const formatPrice = (n) =>
  typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN')}` : '—';

const ActionPill = ({ action }) => {
  const isCall = action === 'call';
  const Icon = isCall ? Phone : MessageCircle;
  const cls = isCall
    ? 'bg-slate-800 text-cyan-300 border border-slate-700'
    : 'bg-green-500/10 text-green-300 border border-green-500/30';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${cls}`}
      data-testid={`lead-action-${action}`}
    >
      <Icon className="w-3 h-3" /> {action}
    </span>
  );
};

const StatTile = ({ label, value, accent = 'cyan', icon: Icon }) => (
  <div className="relative bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 overflow-hidden">
    <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full bg-${accent}-500/10 blur-2xl pointer-events-none`} />
    <div className="relative flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">{label}</div>
        <div className="mt-2 text-2xl font-extrabold text-white">{value}</div>
      </div>
      {Icon && (
        <div className={`w-9 h-9 rounded-xl bg-${accent}-500/10 border border-${accent}-500/20 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 text-${accent}-400`} />
        </div>
      )}
    </div>
  </div>
);

const DealerLeads = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, whatsapp_count: 0, call_count: 0 });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await dealerLeads.list();
      setLeads(res.leads || []);
      setStats({
        total: res.total || 0,
        whatsapp_count: res.whatsapp_count || 0,
        call_count: res.call_count || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (actionFilter !== 'all' && l.action !== actionFilter) return false;
      if (!q) return true;
      const hay = `${l.buyer_name || ''} ${l.buyer_mobile || ''} ${l.car_make || ''} ${l.car_model || ''} ${l.car_year || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [leads, actionFilter, search]);

  return (
    <div data-testid="dealer-leads-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Leads</span>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight">Buyer Leads</h1>
          <p className="mt-1 text-slate-400 text-sm">
            Every buyer who hit <strong className="text-slate-200">Call</strong> or <strong className="text-slate-200">WhatsApp</strong> on your microsite.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="leads-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900/60 border border-slate-700 hover:border-cyan-500/40 text-sm text-slate-300 hover:text-cyan-300 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <StatTile label="Total Leads" value={stats.total} icon={Users} accent="cyan" />
        <StatTile label="WhatsApp" value={stats.whatsapp_count} icon={MessageCircle} accent="green" />
        <StatTile label="Calls" value={stats.call_count} icon={Phone} accent="orange" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, mobile, car…"
            data-testid="leads-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 focus:border-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-slate-500" />
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            data-testid="leads-action-filter"
            className="bg-slate-900/60 border border-slate-700 text-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50"
          >
            <option value="all">All actions</option>
            <option value="whatsapp">WhatsApp only</option>
            <option value="call">Calls only</option>
          </select>
        </div>
      </div>

      {/* Table / empty / loading */}
      {loading ? (
        <div className="py-16 text-center text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-slate-700/60 rounded-2xl bg-slate-900/30">
          <Inbox className="w-10 h-10 mx-auto mb-2 text-slate-600" />
          <p className="text-white font-semibold">No leads yet</p>
          <p className="text-sm text-slate-400 mt-1">
            Leads appear here every time a buyer hits Call or WhatsApp on your site.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leads-table">
                <thead className="bg-slate-900/60 text-slate-400 text-[10px] uppercase tracking-[0.18em]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">When</th>
                    <th className="text-left px-4 py-3 font-semibold">Buyer</th>
                    <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                    <th className="text-left px-4 py-3 font-semibold">Car</th>
                    <th className="text-left px-4 py-3 font-semibold">Price</th>
                    <th className="text-left px-4 py-3 font-semibold">Action</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filtered.map((l) => {
                    const car = [l.car_year, l.car_make, l.car_model].filter(Boolean).join(' ') || '—';
                    return (
                      <tr key={l.id} className="hover:bg-white/5 transition-colors" data-testid={`lead-row-${l.id}`}>
                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{formatDate(l.created_at)}</td>
                        <td className="px-4 py-3 font-semibold text-white">{l.buyer_name}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <a href={`tel:${l.buyer_mobile}`} className="text-cyan-300 hover:underline font-mono">
                            +91 {l.buyer_mobile}
                          </a>
                        </td>
                        <td className="px-4 py-3 text-slate-300">{car}</td>
                        <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{formatPrice(l.car_price)}</td>
                        <td className="px-4 py-3"><ActionPill action={l.action} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <a
                              href={`tel:${l.buyer_mobile}`}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold"
                              data-testid={`lead-call-${l.id}`}
                            >
                              <Phone className="w-3 h-3" /> Call
                            </a>
                            <a
                              href={`https://wa.me/91${l.buyer_mobile}?text=${encodeURIComponent(`Hi ${l.buyer_name}, thanks for your interest in our ${car}. How can I help?`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-300 text-[11px] font-bold"
                              data-testid={`lead-wa-${l.id}`}
                            >
                              <MessageCircle className="w-3 h-3" /> WhatsApp
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {filtered.map((l) => {
              const car = [l.car_year, l.car_make, l.car_model].filter(Boolean).join(' ') || '—';
              return (
                <div
                  key={l.id}
                  data-testid={`lead-card-${l.id}`}
                  className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-white truncate">{l.buyer_name}</div>
                      <a href={`tel:${l.buyer_mobile}`} className="text-cyan-300 text-sm font-mono">
                        +91 {l.buyer_mobile}
                      </a>
                    </div>
                    <ActionPill action={l.action} />
                  </div>
                  <div className="text-xs text-slate-400">
                    <span className="text-slate-500 uppercase tracking-wider mr-1">Interested in:</span>
                    <span className="text-slate-200">{car}</span>
                    <span className="text-slate-500"> · {formatPrice(l.car_price)}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{formatDate(l.created_at)}</div>
                  <div className="flex gap-2 pt-1">
                    <a
                      href={`tel:${l.buyer_mobile}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-200 text-xs font-bold"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call back
                    </a>
                    <a
                      href={`https://wa.me/91${l.buyer_mobile}?text=${encodeURIComponent(`Hi ${l.buyer_name}, thanks for your interest in our ${car}. How can I help?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-500/20 text-green-300 text-xs font-bold"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default DealerLeads;
